import axios from "axios";
import { toast } from "sonner";

// MODO DEMO (sep-2026): `@/demo/modo` es un módulo INERTE en producción
// (DEMO_ACTIVO === false, instalarDemo() no hace nada). El build demo lo sustituye por
// `src/demo/modo.demo.js` vía webpack, y ahí sí se instala el adaptador que resuelve toda
// la red con fixtures locales. Producción queda idéntica.
import { DEMO_ACTIVO, instalarDemo } from "@/demo/modo";

const BACKEND_URL = DEMO_ACTIVO ? "" : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

instalarDemo(api);

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cos_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Expulsión global ante 401 (ago-2026) ──────────────────────────────────
// Antes de este bloque, un 401 de sesión (token expirado, sesión invalidada por
// `token_version`, usuario desactivado) no se manejaba en ningún lado: el `user` seguía
// vivo en memoria, el menú renderizaba y cada pantalla se quedaba en su propio spinner
// infinito sin explicar nada. Ahora cualquier 401 en una ruta con sesión borra el token,
// avisa con un mensaje claro y manda al login.
//
// NO aplica a las rutas anónimas (login, reto MFA, reset, verificación): ahí un 401 es un
// error de negocio normal ("Credenciales incorrectas", "Código MFA inválido") que la propia
// pantalla debe mostrar sin expulsar a nadie.
const RUTAS_ANONIMAS = [
  "/auth/login",            // cubre /auth/login y /auth/login-mfa/*
  "/auth/register-entity",
  "/auth/solicitar-reset",
  "/auth/validar-token",
  "/auth/definir-password",
  "/auth/verificar-correo",
  "/auth/reenviar-verificacion",
];
const MSG_SESION_KEY = "cos_sesion_msg";
const MSG_SESION_DEFAULT = "Tu sesión terminó. Vuelve a iniciar sesión.";
let expulsando = false;

const esRutaAnonima = (url = "") => RUTAS_ANONIMAS.some((r) => url.includes(r));

function expulsarPorSesionInvalida(mensaje) {
  if (expulsando) return;
  expulsando = true;
  localStorage.removeItem("cos_token");
  // El aviso viaja en sessionStorage porque la salida es una recarga dura: es la única
  // forma de matar de raíz los spinners y los estados de React de la sesión caída.
  try { sessionStorage.setItem(MSG_SESION_KEY, mensaje || MSG_SESION_DEFAULT); } catch (_e) { /* modo privado */ }
  window.location.replace("/login");
}

// Rehidratación del aviso tras la recarga: 400 ms es suficiente para que el <Toaster/>
// global de App.js esté montado y el toast no se pierda.
if (typeof window !== "undefined") {
  setTimeout(() => {
    let msg = null;
    try {
      msg = sessionStorage.getItem(MSG_SESION_KEY);
      sessionStorage.removeItem(MSG_SESION_KEY);
    } catch (_e) { /* modo privado */ }
    if (msg) toast.error(msg, { duration: 9000, id: "sesion-invalidada" });
  }, 400);
}

// Feb-2026 · Fix P0 (iteration_101): un backend que responde 4xx con `detail=objeto`
// (por ejemplo el gate EBR `{codigo:"ebr_no_aprobada", mensaje:"..."}`) rompía la pantalla
// con "Objects are not valid as a React child" cuando el llamador hacía
// `toast.error(err.response.data.detail)` directo (~129 sitios). Este interceptor de RESPUESTA
// NORMALIZA `err.response.data.detail` a STRING en TODA la app, de forma que ningún
// consumidor legacy pueda renderizar accidentalmente un objeto. Además expone el objeto
// original en `err.response.data.detail_raw` por si un flujo específico quiere leer el `codigo`.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const data = err?.response?.data;
      if (data && "detail" in data) {
        const original = data.detail;
        let normalized;
        if (original == null) normalized = "";
        else if (typeof original === "string") normalized = original;
        else if (Array.isArray(original)) {
          normalized = original
            .map((e) => (e && typeof e.msg === "string" ? e.msg : (typeof e === "string" ? e : JSON.stringify(e))))
            .join(" · ");
        } else if (typeof original === "object") {
          normalized = (typeof original.mensaje === "string" && original.mensaje)
            || (typeof original.message === "string" && original.message)
            || (typeof original.msg === "string" && original.msg)
            || "Ocurrió un error. Intenta de nuevo.";
        } else {
          normalized = String(original);
        }
        data.detail_raw = original;
        data.detail = normalized;
      }
    } catch (_e) { /* nunca romper el pipeline */ }
    try {
      if (err?.response?.status === 401
          && !esRutaAnonima(err?.config?.url || "")
          && localStorage.getItem("cos_token")) {
        const detalle = err?.response?.data?.detail;
        expulsarPorSesionInvalida(
          (typeof detalle === "string" && detalle) || MSG_SESION_DEFAULT,
        );
      }
    } catch (_e) { /* nunca romper el pipeline */ }
    return Promise.reject(err);
  },
);

export function apiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  // Payloads estructurados con {codigo, mensaje} (ej. gates 403/409)
  if (detail && typeof detail === "object" && typeof detail.mensaje === "string") return detail.mensaje;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Generic CRUD helpers
export const listResource = (name, params = {}) => api.get(`/${name}`, { params }).then((r) => r.data);
export const getResource = (name, id) => api.get(`/${name}/${id}`).then((r) => r.data);
export const createResource = (name, body) => api.post(`/${name}`, body).then((r) => r.data);
export const updateResource = (name, id, body) => api.put(`/${name}/${id}`, body).then((r) => r.data);
export const deleteResource = (name, id) => api.delete(`/${name}/${id}`).then((r) => r.data);
